import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AccountScreen } from "./AccountScreen";
import { ActionLinkPage } from "./ActionLinkPage";
import { CaregiverAccept } from "./CaregiverAccept";
import { CostEstimateScreen } from "./CostEstimateScreen";
import { HomeScreen } from "./HomeScreen";
import { MedicationsScreen } from "./MedicationsScreen";
import { Portal } from "./Portal";
import { ProgramsScreen } from "./ProgramsScreen";
import { SettingsScreen } from "./SettingsScreen";

/**
 * Member-portal routes. `Portal` gates on login for every path under it (the
 * login, OTP and handoff steps are path-agnostic, so the member lands on the
 * path they opened) and, signed in, renders the dashboard layout whose outlet
 * holds the screen: Home at `/`, My Programs at `/programs`, My Medications at
 * `/medications`, Cost Estimate at `/cost-estimate`, Account at `/account` and
 * Settings at `/settings`, which share one dashboard fetch. Messages link to
 * `/forms/:id` and `/forms/:id/sign` (the e-sign notification builds the
 * latter), so both redirect to `/`, where Home carries the e-sign flow. A
 * document's signature request links to
 * `/forms/:enrollmentId/sign/:documentSlug` (CMS-1696 is `cms-1696`), which
 * renders `Portal` with no outlet screen: the same login gate keeps the link,
 * and once signed in the dashboard layout shows that document's sign page.
 * The caregiver-invitation page at `/caregiver/accept` is a sibling of
 * `Portal`, outside the login gate: the emailed link lands there with no
 * session and must not redirect to the login.
 *
 * `/do` is the mid-call action-link page and needs no portal session, like
 * the login screen: the `#action=<token>` fragment is its only authorization.
 */
export function App(): ReactNode {
  return (
    <Routes>
      <Route path="/" element={<Portal />}>
        <Route index element={<HomeScreen />} />
        <Route path="programs" element={<ProgramsScreen />} />
        <Route path="medications" element={<MedicationsScreen />} />
        <Route path="cost-estimate" element={<CostEstimateScreen />} />
        <Route path="account" element={<AccountScreen />} />
        <Route path="settings" element={<SettingsScreen />} />
      </Route>
      <Route path="/caregiver/accept" element={<CaregiverAccept />} />
      <Route path="/do" element={<ActionLinkPage />} />
      <Route path="/forms/:id" element={<Navigate to="/" replace />} />
      <Route path="/forms/:id/sign" element={<Navigate to="/" replace />} />
      <Route path="/forms/:enrollmentId/sign/:documentSlug" element={<Portal />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
